import { Injectable } from '@angular/core'

import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'

import { LaskunLokalisoituTila } from '../../laskut/laskut.firestore.datasource'

import { LemonTranslationService } from '../../_jaettu-angular/service/lemon-translation.service'

import { Lasku, LaskunTila, LaskunumeroTyyppi, LaskutettavatPalvelut } from '@lemon/core-firebase/model/lasku'
import { LaskuSharedService } from '@lemon/core-firebase/service/lasku/lasku-shared.service'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { DateService } from '@lemon/core/service/date.service'

import { AsiakasService } from '../../_angular/service/asiakas/asiakas.service'
import { LaskuService } from '../../_angular/service/lasku/lasku.service'
import { Asiakas, AsiakkaanSopimuskaudenLopettamisenSyy, AsiakkaanSopimuskaudenTila, AsiakkaanSopimuskausi } from '../../_jaettu-lemonator/model/asiakas'
import { AsiakkaanSopimuskausiService } from '../../_jaettu-lemonator/service/sopimuskausi.service'

import { LaskuIndeksoija } from '@lemon/core-firebase/service/lasku/lasku.indeksoija'
import { LocalDate, Timestamp } from '@lemon/core/model/common'
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs'
import { map, shareReplay, switchMap } from 'rxjs/operators'
import { FirebaseLemonaid } from '../../_angular/service/firebase-lemonator.service'

export interface TyokieltoListaRivi {
  asiakasAvain: string
  asiakasNimi: string
  laskujenMaara: number
  laskut: Lasku[]
  avoinSumma?: number
  lopetusPvm?: LocalDate
  lopettamisenSyy?: AsiakkaanSopimuskaudenLopettamisenSyy
  tyokielto?: boolean
  tyokieltoAlku?: Timestamp
}

export interface JaetutTyokieltoAsiakkaat {
  aktiiviset: TyokieltoListaRivi[]
  lopettaneet: TyokieltoListaRivi[]
}

export interface TyokieltoStatus {
  tyokielto: boolean
  tyokieltoAlku: Timestamp | undefined
}

@Injectable()
export class MaksumuistutusService {
  private _deletedInvoices = new BehaviorSubject<Set<string>>(new Set<string>())
  private _piilotetutTyokieltoAsiakkaat = new BehaviorSubject<Set<string>>(new Set<string>())
  private _piilotetutTyokiellossaOlevatAsiakkaat = new BehaviorSubject<Set<string>>(new Set<string>())
  private _piilotetutLopettaneetAsiakkaat = new BehaviorSubject<Set<string>>(new Set<string>())

  private _laskunTilaSubject = new BehaviorSubject<LaskunLokalisoituTila[]>([])
  laskunTilaObservable: Observable<LaskunLokalisoituTila[]> = this._laskunTilaSubject.asObservable()

  readonly rawLaskut$: Observable<Lasku[]>

  // Tab 0 — 1. muistutus (0 aiempaa muistutusta, minimiSumma 10 €)
  readonly ensimmainenMuistutus$: Observable<Lasku[]>

  // Tab 1 — 2. muistutus (1 aiempi muistutus, minimiSumma 50 €)
  readonly toinenMuistutus$: Observable<Lasku[]>

  // Tab 2 — Työkiellossa olevat
  readonly tyokieltoAsiakkaat$: Observable<TyokieltoListaRivi[]>

  // Tab 3+4 — Työkielto asetettava & Purku-uhka
  readonly tyokieltoKaikkiRivit$: Observable<TyokieltoListaRivi[]>

  // Tab 5 — Lopettaneet, joilla työkielto edelleen päällä
  readonly lopettaneetTyokieltoAsiakkaat$: Observable<TyokieltoListaRivi[]>

  // Työkielto-status kaikille relevanteille asiakkaille
  readonly tyokieltoStatusMap$: Observable<Map<string, TyokieltoStatus>>

  constructor(
    private _firebaseLemonaid: FirebaseLemonaid,
    private _dateService: DateService,
    private _lemonTranslationService: LemonTranslationService,
    private _laskuService: LaskuService,
    private _laskuSharedService: LaskuSharedService,
    private _laskuIndeksoija: LaskuIndeksoija,
    private _asiakasService: AsiakasService,
    private _sopimuskausiService: AsiakkaanSopimuskausiService,
    private _laskuUriService: LaskuUriService,
  ) {
    this._lemonTranslationService.currentLanguageObservable.subscribe(kieli => {
      const naytettavatTilat: string[] = []

      naytettavatTilat.push(LaskunTila.kaikki)
      naytettavatTilat.push(LaskunTila.avoin)
      naytettavatTilat.push(LaskunTila.luottotappio)
      naytettavatTilat.push(LaskunTila.maksettu)
      naytettavatTilat.push(LaskunTila.eraantynyt)
      naytettavatTilat.push(LaskunTila.maksettuLiikaa)
      naytettavatTilat.push(LaskunTila.luonnos)
      naytettavatTilat.push(LaskunTila.poistettu)
      naytettavatTilat.push(LaskunTila.mitatoity)

      const tilat: LaskunLokalisoituTila[] = []

      for (const tila of naytettavatTilat) {
        tilat.push({
          tunnus: tila,
          nimi: this._lemonTranslationService.lokalisoiKielella('lasku.listaus.tila.' + tila, kieli),
        })
      }

      tilat.sort((a, b): number => {
        if (a.tunnus === LaskunTila.kaikki) {
          return -1
        } else if (b.tunnus === LaskunTila.kaikki) {
          return 1
        }
        return a.nimi.localeCompare(b.nimi)
      })

      this._laskunTilaSubject.next(tilat)
    })

    // Yksi jaettu Firestore-kuuntelija kaikille välilehdille
    this.rawLaskut$ = this._firebaseLemonaid
      .firestoreCollection<Lasku>(this._laskuUriService.getCustomerLaskutCollectionUri(this._laskuService.LEMONTREE_ASIAKAS_AVAIN))
      .where('tila', '==', LaskunTila.eraantynyt)
      .listen()
      .pipe(
        map(laskut => {
          for (const lasku of laskut) {
            this._laskuSharedService.poistaKaikkiLaskunHakuKentatMuistista(lasku)
          }
          return laskut
        }),
        shareReplay({ bufferSize: 1, refCount: true }),
      )

    // Piilotetut laskut luetaan synkronisesti .value:sta, jotta piilottaminen
    // ei triggeröi koko pipelinen uudelleenajoa (ja siten uudelleenlajittelua).
    // Komponentti hoitaa piilotuksen suoraan data sourcesta.
    this.ensimmainenMuistutus$ = this.rawLaskut$.pipe(
      map(invoices => {
        const deletedSet = this._deletedInvoices.value
        return invoices.filter(lasku => {
          if (!lasku.avoinnaRypas || lasku.avoinnaRypas < 10) {
            return false
          }
          if (deletedSet.has(lasku.avain)) {
            return false
          }
          const liittyvatLaskut = this._laskuSharedService.annaLiittyvatLaskut(lasku)
          const muistutusLaskujenMaara = liittyvatLaskut.filter(liittyva => liittyva.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS)?.length
          return muistutusLaskujenMaara === 0
        })
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.toinenMuistutus$ = this.rawLaskut$.pipe(
      map(invoices => {
        const deletedSet = this._deletedInvoices.value
        return invoices.filter(lasku => {
          if (!lasku.avoinnaRypas || lasku.avoinnaRypas < 50) {
            return false
          }
          if (deletedSet.has(lasku.avain)) {
            return false
          }
          const liittyvatLaskut = this._laskuSharedService.annaLiittyvatLaskut(lasku)
          const muistutusLaskujenMaara = liittyvatLaskut.filter(liittyva => liittyva.nrotyyppi === LaskunumeroTyyppi.MUISTUTUS)?.length
          return muistutusLaskujenMaara === 1
        })
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    const tyokieltoKaikkiRivitRaw$ = this.rawLaskut$.pipe(
      map(invoices => this._groupInvoicesByAsiakas(invoices)),
      switchMap(byAsiakas => this._fetchAsiakasMap(byAsiakas)),
      map(({ byAsiakas, asiakasMap }) => this._filterActiveAsiakkaat(byAsiakas, asiakasMap)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.tyokieltoKaikkiRivit$ = tyokieltoKaikkiRivitRaw$.pipe(
      map(rivit => {
        const piilotetut = this._piilotetutTyokieltoAsiakkaat.value
        return rivit.filter(rivi => !piilotetut.has(rivi.asiakasAvain))
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    const jaetutTyokieltoAsiakkaat$ = combineLatest([this._asiakasService.annaTyokieltoAsiakkaatObservable(), this.rawLaskut$]).pipe(
      map(([tyokieltoAsiakkaat, invoices]) => this._jaaTyokieltoAsiakkaat(tyokieltoAsiakkaat, invoices)),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.tyokieltoAsiakkaat$ = jaetutTyokieltoAsiakkaat$.pipe(
      map(({ aktiiviset }) => {
        const piilotetut = this._piilotetutTyokiellossaOlevatAsiakkaat.value
        return aktiiviset.filter(rivi => !piilotetut.has(rivi.asiakasAvain))
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    this.lopettaneetTyokieltoAsiakkaat$ = jaetutTyokieltoAsiakkaat$.pipe(
      map(({ lopettaneet }) => {
        const piilotetut = this._piilotetutLopettaneetAsiakkaat.value
        return lopettaneet.filter(rivi => !piilotetut.has(rivi.asiakasAvain))
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )

    // Yhdistetään kaikkien työkielto-välilehtien asiakasavaimet ja haetaan status kerralla
    this.tyokieltoStatusMap$ = combineLatest([this.tyokieltoKaikkiRivit$, this.tyokieltoAsiakkaat$]).pipe(
      map(([kaikkiRivit, tyokieltoRivit]) => {
        const avaimet = new Set<string>()
        for (const rivi of kaikkiRivit) {
          avaimet.add(rivi.asiakasAvain)
        }
        for (const rivi of tyokieltoRivit) {
          avaimet.add(rivi.asiakasAvain)
        }
        return Array.from(avaimet)
      }),
      switchMap(avaimet => {
        if (avaimet.length === 0) {
          return of(new Map<string, TyokieltoStatus>())
        }
        return combineLatest(avaimet.map(avain => this._asiakasService.annaAsiakasObservable(avain).pipe(map(asiakas => ({ avain, asiakas }))))).pipe(
          map(tulokset => {
            const statusMap = new Map<string, TyokieltoStatus>()
            for (const { avain, asiakas } of tulokset) {
              statusMap.set(avain, {
                tyokielto: asiakas?.tyokielto ?? false,
                tyokieltoAlku: asiakas?.tyokieltoAlku,
              })
            }
            return statusMap
          }),
        )
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
  }

  asetaSortDataTaulukolle(dataSource: MatTableDataSource<Lasku>) {
    const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
    dataSource.sortData = (data: Lasku[], sort: MatSort): Lasku[] => {
      const active = sort.active
      const direction = sort.direction

      if (!active || direction === '') {
        return data
      }

      const directionMultiplier = direction === 'asc' ? 1 : -1

      if (active === 'summa') {
        return data.sort((a, b) => {
          const ka = this._laskuSharedService.annaViimeisinKasiteltavaLasku(a)
          const kb = this._laskuSharedService.annaViimeisinKasiteltavaLasku(b)
          const valuea = ka.summa
          const valueb = kb.summa
          if (valuea === null && valueb === null) {
            return 0
          } else if (valuea === null) {
            return 1 * directionMultiplier
          } else if (valueb === null) {
            return -1 * directionMultiplier
          }
          return (valuea - valueb) * directionMultiplier
        })
      }

      if (active === 'avoinna') {
        return data.sort((a, b) => {
          const ka = this._laskuSharedService.annaViimeisinKasiteltavaLasku(a)
          const kb = this._laskuSharedService.annaViimeisinKasiteltavaLasku(b)
          const valuea = ka.avoinna
          const valueb = kb.avoinna
          if (valuea === null && valueb === null) {
            return 0
          } else if (valuea === null) {
            return 1 * directionMultiplier
          } else if (valueb === null) {
            return -1 * directionMultiplier
          }
          return (valuea - valueb) * directionMultiplier
        })
      }

      if (active === 'nro') {
        return data.sort((a, b) => {
          const valuea = this._laskuIndeksoija.annaNumeroLajitteluaVarten(a)
          const valueb = this._laskuIndeksoija.annaNumeroLajitteluaVarten(b)
          if (valuea === null && valueb === null) {
            return 0
          } else if (valuea === null) {
            return 1 * directionMultiplier
          } else if (valueb === null) {
            return -1 * directionMultiplier
          }
          return (valuea - valueb) * directionMultiplier
        })
      }

      if (active === 'pvm') {
        return data.sort((a, b) => {
          const ka = this._laskuSharedService.annaViimeisinKasiteltavaLasku(a)
          const kb = this._laskuSharedService.annaViimeisinKasiteltavaLasku(b)
          const valuea = ka.pvm ? ka.pvm.toDate() : null
          const valueb = kb.pvm ? kb.pvm.toDate() : null
          if (valuea === null && valueb === null) {
            return 0
          } else if (valuea === null) {
            return 1 * directionMultiplier
          } else if (valueb === null) {
            return -1 * directionMultiplier
          }
          return (valuea.getTime() - valueb.getTime()) * directionMultiplier
        })
      }

      if (active === 'erapvm') {
        return data.sort((a, b) => {
          const ka = this._laskuSharedService.annaViimeisinKasiteltavaLasku(a)
          const kb = this._laskuSharedService.annaViimeisinKasiteltavaLasku(b)
          const valuea = ka.pvm ? ka.erapvm.toDate() : null
          const valueb = kb.pvm ? kb.erapvm.toDate() : null
          if (valuea === null && valueb === null) {
            return 0
          } else if (valuea === null) {
            return 1 * directionMultiplier
          } else if (valueb === null) {
            return -1 * directionMultiplier
          }
          return (valuea.getTime() - valueb.getTime()) * directionMultiplier
        })
      }

      if (active === 'tila') {
        return data.sort((a, b) => {
          const valuea = a.tila
          const valueb = b.tila
          return collator.compare(valuea, valueb) * directionMultiplier
        })
      }

      return data
    }
  }

  deleteInvoice(invoiceId: string) {
    this._deletedInvoices.value.add(invoiceId)
    this._deletedInvoices.next(this._deletedInvoices.value)
  }

  deleteAllInvoices(keepAvain: string, kaikkiLaskut: Lasku[]) {
    for (const lasku of kaikkiLaskut) {
      if (keepAvain !== lasku.avain) {
        this._deletedInvoices.value.add(lasku.avain)
      }
    }
    this._deletedInvoices.next(this._deletedInvoices.value)
  }

  piilotaTyokiellossaOlevaAsiakas(asiakasAvain: string) {
    this._piilotetutTyokiellossaOlevatAsiakkaat.value.add(asiakasAvain)
    this._piilotetutTyokiellossaOlevatAsiakkaat.next(this._piilotetutTyokiellossaOlevatAsiakkaat.value)
  }

  piilotaKaikkiMuutTyokiellossaOlevatAsiakkaat(sailytettavaAvain: string, kaikkiRivit: TyokieltoListaRivi[]) {
    for (const rivi of kaikkiRivit) {
      if (rivi.asiakasAvain !== sailytettavaAvain) {
        this._piilotetutTyokiellossaOlevatAsiakkaat.value.add(rivi.asiakasAvain)
      }
    }
    this._piilotetutTyokiellossaOlevatAsiakkaat.next(this._piilotetutTyokiellossaOlevatAsiakkaat.value)
  }

  piilotaLopettanutAsiakas(asiakasAvain: string) {
    this._piilotetutLopettaneetAsiakkaat.value.add(asiakasAvain)
    this._piilotetutLopettaneetAsiakkaat.next(this._piilotetutLopettaneetAsiakkaat.value)
  }

  piilotaKaikkiMuutLopettaneetAsiakkaat(sailytettavaAvain: string, kaikkiRivit: TyokieltoListaRivi[]) {
    for (const rivi of kaikkiRivit) {
      if (rivi.asiakasAvain !== sailytettavaAvain) {
        this._piilotetutLopettaneetAsiakkaat.value.add(rivi.asiakasAvain)
      }
    }
    this._piilotetutLopettaneetAsiakkaat.next(this._piilotetutLopettaneetAsiakkaat.value)
  }

  piilotaTyokieltoAsiakas(asiakasAvain: string) {
    this._piilotetutTyokieltoAsiakkaat.value.add(asiakasAvain)
    this._piilotetutTyokieltoAsiakkaat.next(this._piilotetutTyokieltoAsiakkaat.value)
  }

  piilotaKaikkiMuutTyokieltoAsiakkaat(sailytettavaAvain: string, kaikkiRivit: TyokieltoListaRivi[]) {
    for (const rivi of kaikkiRivit) {
      if (rivi.asiakasAvain !== sailytettavaAvain) {
        this._piilotetutTyokieltoAsiakkaat.value.add(rivi.asiakasAvain)
      }
    }
    this._piilotetutTyokieltoAsiakkaat.next(this._piilotetutTyokieltoAsiakkaat.value)
  }

  private _groupInvoicesByAsiakas(invoices: Lasku[]): Map<string, Lasku[]> {
    const byAsiakas = new Map<string, Lasku[]>()
    const kuukausilaskutusAvaimet = new Set([LaskutettavatPalvelut.JATKUVA_KUUKAUSILASKUTUS, LaskutettavatPalvelut.JATKUVA_KUUKAUSILASKUTUS_KORJAUS])
    for (const lasku of invoices) {
      if (!lasku.asiakas?.avain) {
        continue
      }
      // Suodatetaan vain kuukausilaskutuksen laskut
      const onKuukausilaskutus = lasku.tuotteet?.some(rivi => kuukausilaskutusAvaimet.has(rivi.tuote?.avain as LaskutettavatPalvelut))
      if (!onKuukausilaskutus) {
        continue
      }
      const avain = lasku.asiakas.avain
      if (!byAsiakas.has(avain)) {
        byAsiakas.set(avain, [])
      }
      byAsiakas.get(avain).push(lasku)
    }
    return byAsiakas
  }

  private _fetchAsiakasMap(byAsiakas: Map<string, Lasku[]>) {
    const avaimet = Array.from(byAsiakas.keys())
    if (avaimet.length === 0) {
      return of({ byAsiakas, asiakasMap: new Map<string, Asiakas>() })
    }
    return combineLatest(avaimet.map(avain => this._asiakasService.annaAsiakasObservable(avain))).pipe(
      map(asiakkaat => {
        const asiakasMap = new Map<string, Asiakas>()
        for (const asiakas of asiakkaat) {
          if (asiakas?.avain) {
            asiakasMap.set(asiakas.avain, asiakas)
          }
        }
        return { byAsiakas, asiakasMap }
      }),
    )
  }

  private _jaaTyokieltoAsiakkaat(tyokieltoAsiakkaat: Asiakas[], invoices: Lasku[]): JaetutTyokieltoAsiakkaat {
    const today = this._dateService.currentLocalDate()
    const byAsiakas = this._groupInvoicesByAsiakas(invoices)
    const kaikkiEraantyneetByAsiakas = this._groupAllOverdueInvoicesByAsiakas(invoices)
    const aktiiviset: TyokieltoListaRivi[] = []
    const lopettaneet: TyokieltoListaRivi[] = []

    for (const asiakas of tyokieltoAsiakkaat) {
      const sopimuskausi = this._sopimuskausiService.annaKausiPaivamaaralle(asiakas.sopimuskaudet, today)

      if (sopimuskausi) {
        const laskut = byAsiakas.get(asiakas.avain) ?? []
        aktiiviset.push({
          asiakasAvain: asiakas.avain,
          asiakasNimi: asiakas.nimi,
          laskujenMaara: laskut.length,
          laskut,
        })
        continue
      }

      // Päättynyt kausi on ainoa todiste lopettamisesta: ilman sitä sopimus ei ole vielä alkanut tai sitä ei ole lainkaan, eikä asiakas kuulu millekään työkielto-välilehdelle
      const paattynytKausi = this._sopimuskausiService.annaEdellinenKausi(asiakas.sopimuskaudet, today)
      if (!paattynytKausi) {
        continue
      }

      lopettaneet.push(this._annaLopettaneenRivi(asiakas, paattynytKausi, kaikkiEraantyneetByAsiakas))
    }

    return { aktiiviset, lopettaneet }
  }

  private _annaLopettaneenRivi(asiakas: Asiakas, paattynytKausi: AsiakkaanSopimuskausi, kaikkiEraantyneetByAsiakas: Map<string, Lasku[]>): TyokieltoListaRivi {
    const laskut = kaikkiEraantyneetByAsiakas.get(asiakas.avain) ?? []
    return {
      asiakasAvain: asiakas.avain,
      asiakasNimi: asiakas.nimi,
      laskujenMaara: laskut.length,
      laskut,
      avoinSumma: laskut.reduce((summa, lasku) => summa + (lasku.avoinnaRypas ?? 0), 0),
      lopetusPvm: paattynytKausi.loppuu,
      lopettamisenSyy: paattynytKausi.lopettamisenSyy,
      tyokielto: true,
      tyokieltoAlku: asiakas.tyokieltoAlku,
    }
  }

  private _groupAllOverdueInvoicesByAsiakas(invoices: Lasku[]): Map<string, Lasku[]> {
    const byAsiakas = new Map<string, Lasku[]>()
    for (const lasku of invoices) {
      if (!lasku.asiakas?.avain) {
        continue
      }
      const avain = lasku.asiakas.avain
      if (!byAsiakas.has(avain)) {
        byAsiakas.set(avain, [])
      }
      byAsiakas.get(avain).push(lasku)
    }
    return byAsiakas
  }

  private _filterActiveAsiakkaat(byAsiakas: Map<string, Lasku[]>, asiakasMap: Map<string, Asiakas>): TyokieltoListaRivi[] {
    const today = this._dateService.currentLocalDate()
    const rivit: TyokieltoListaRivi[] = []
    byAsiakas.forEach((laskut, asiakasAvain) => {
      // Suodatetaan pois lopettaneet asiakkaat
      const asiakas = asiakasMap.get(asiakasAvain)
      const sopimuskausi = asiakas ? this._sopimuskausiService.annaKausiPaivamaaralle(asiakas.sopimuskaudet, today) : null
      const sopimuskaudenTila = sopimuskausi ? this._sopimuskausiService.annaSopimuskaudenTilaPoislukienAllekirjoitustaVaativat(sopimuskausi, today) : null
      if (!sopimuskausi || sopimuskaudenTila === AsiakkaanSopimuskaudenTila.PAATTYNYT || sopimuskaudenTila === AsiakkaanSopimuskaudenTila.PURETTU) {
        return
      }
      rivit.push({
        asiakasAvain,
        asiakasNimi: laskut[0].asiakas.nimi,
        laskujenMaara: laskut.length,
        laskut,
      })
    })
    return rivit
  }
}
